import { reduce } from 'lodash';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { price, toSentence } from 'utils/helpers';

const groupCustomerFacingAdjustments = (adjustments) => {
  const groups = {};
  adjustments
    .filter((adj) => adj.display_to_customer)
    .forEach((adj) => {
      const key = adj.adjustment_definition_name || 'Surcharge';
      if (!groups[key]) {
        groups[key] = {
          adjustment_definition_name: adj.adjustment_definition_name,
          gross_in_cents: 0,
        };
      }
      groups[key].gross_in_cents += adj.gross_in_cents;
    });
  return Object.values(groups);
};

export default function Price({ date, resource, extraPicks, allResources }) {
  const resourceIds = [resource, ...extraPicks];
  const pricingTotals = reduce(
    resourceIds,
    (
      { priceInCents, discountInCents, rulesApplied, adjustments },
      resourceId
    ) => {
      const newResource = allResources[parseInt(resourceId)];
      const newBookability = newResource?.bookability?.[date];

      const newPrice =
        parseInt(
          newBookability?.pricing_details
            ?.minimum_price_with_adjustments_in_cents
        ) || 0;

      const newDiscounts =
        parseInt(newBookability?.pricing_details?.total_adjustments_in_cents) *
          -1 || 0;

      const appliedRules = newBookability?.pricing_details?.applied_rules || [];

      const newAdjustments = newBookability?.pricing_details?.adjustments || [];

      return {
        priceInCents: priceInCents + newPrice,
        discountInCents: discountInCents + newDiscounts,
        rulesApplied: [...rulesApplied, ...appliedRules],
        adjustments: [...adjustments, ...newAdjustments],
      };
    },
    { priceInCents: 0, discountInCents: 0, rulesApplied: [], adjustments: [] }
  );

  const { priceInCents, discountInCents, rulesApplied, adjustments } =
    pricingTotals;

  // Hide if no price
  if (!priceInCents) {
    return null;
  }

  const customerFacingAdjustments = groupCustomerFacingAdjustments(adjustments);

  const savingMessage =
    discountInCents <= 0 ? null : (
      <div className="text-x-small text-success mt-2">
        You saved {price(discountInCents)} with{' '}
        {pluralize('special', rulesApplied.length)}: {toSentence(rulesApplied)}
      </div>
    );

  return (
    <div>
      <div className="text-x-large font-bold">
        Total <span>{price(priceInCents)}</span>
      </div>
      {customerFacingAdjustments.map((group) => (
        <div
          key={group.adjustment_definition_name || 'Surcharge'}
          className="text-x-small text-danger mt-2"
        >
          {price(group.gross_in_cents)} (
          {group.adjustment_definition_name || 'Surcharge'})
        </div>
      ))}
      {savingMessage}
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
Price.propTypes = {
  date: PropTypes.string,
  resource: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  extraPicks: PropTypes.arrayOf(PropTypes.string).isRequired,
  allResources: PropTypes.object.isRequired,
};
